import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router'
import { Suspense, useEffect, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { UsersIcon, PlusIcon, FileTextIcon } from 'lucide-react'
import { trpc } from '@/main'
import { useTeam } from '@/hooks/use-team'
import { usePermissions } from '@/hooks/use-permissions'
import { clearProfileSelection, useRootSearch } from '@/hooks/use-root-search'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button, ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command'
import { SectionLabel } from '@/components/custom/section-label'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ListPanelBodySkeleton, MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ReportPager } from './-components/report/report-pager'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { NavStackStage, NavStackScreen, useStackedLayout } from '@/components/custom/route-transition/stack/nav-stack'
import { SectionStack } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { Avatar } from './-components/shared/avatar'
import { ReportTabs } from './-components/report/report-tabs'
import { TourDialog } from './-components/teams/tour-dialog'
import { TeamTreeItem } from './-components/teams/team-tree-item'
import { TeamsMembersPanel } from './-components/teams/teams-members-panel'
import { TeamActionMenu } from './-components/teams/team-action-menu'
import { TeamForm } from './-components/teams/team-form'

export const Route = createFileRoute('/_app/teams')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.teams.getTeams.queryOptions({}))
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()
  // Inside a report-tab live preview (the pager's shadow router pinned to e.g. /teams/42/board), strip the
  // whole list/members/report split down to just the pager pane, so the preview shows the destination tab and
  // not a second copy of the Teams section. Only for a surface BELOW this one: the top-level section pager
  // previews from '/', which is ABOVE us and wants the full Teams section (see usePreviewMode).
  if (usePreviewMode('/teams') === 'passthrough') return <ReportPager className="h-full" />

  return (
    <div id="report" className="@container flex h-full flex-col">
      <TourDialog />
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the teams list, the team's
          members, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <TeamsStack>
        <PageLayout
          icon={NAVIGATION_ICONS.teams}
          title={t`Teams`}
          breadcrumbsSlot={<TeamsBreadcrumbs />}
          titleActions={<TeamsTitleActions />}
        >
          <TeamsLayout />
        </PageLayout>
      </TeamsStack>
    </div>
  )
}

/**
 * Teams' mobile hierarchy — list → the team's members → its report. The standard section shape, so the whole
 * declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function TeamsStack(props: { children: React.ReactNode }) {
  const { teamId } = useParams({ strict: false })
  return (
    <SectionStack list={{ to: '/teams' }} detail={teamId ? { to: '/teams/$teamId', params: { teamId } } : null}>
      {props.children}
    </SectionStack>
  )
}

function TeamsLayout() {
  const { t } = useLingui()
  const { teamId } = useParams({ strict: false })

  const hasTeamSelected = !!teamId

  const teamDialog = useSearchParamDialog('editTeam')
  const editTeamId = typeof teamDialog.value === 'number' ? teamDialog.value.toString() : undefined

  // No viewport-driven auto-collapse any more: the panes used to be squashed to a 0px list and a 52px avatar
  // rail so all three could share one phone screen. The nav stack gives each of them the WHOLE screen and
  // decides which one is on it, so both providers stay expanded and the collapse is desktop's business only
  // (where `isMobile` was false regardless — this is a no-op there).
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      <Suspense fallback={null}>
        {teamDialog.mounted && (
          <Dialog open={teamDialog.open} onOpenChange={teamDialog.onOpenChange}>
            <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
              <TeamForm teamId={editTeamId} onOpenChange={teamDialog.onOpenChange} />
            </DialogContent>
          </Dialog>
        )}
      </Suspense>

      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Teams list (always visible on desktop, squashes to a 52px icon column when a team is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <NavStackScreen index={0} title={t`Teams`}>
          <CollapsibleListPanel isOpen={hasTeamSelected} className="print:hidden">
            <CollapsibleListPanelBorder>
              <CollapsiblePanel expandedWidth="100%" disableTransition>
                {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. */}
                <CollapsiblePanelContent>
                  <Suspense fallback={<ListPanelBodySkeleton />}>
                    <TeamsPanelContent variant="sidebar" />
                  </Suspense>
                </CollapsiblePanelContent>
              </CollapsiblePanel>
            </CollapsibleListPanelBorder>
          </CollapsibleListPanel>
        </NavStackScreen>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `ns-passthrough`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasTeamSelected} innerClassName="flex" className="ns-passthrough">
          {/* LEVEL 1 — the team's members. The first screen a team opens onto on mobile. */}
          <NavStackScreen
            index={1}
            title={<TeamScreenTitle />}
            actions={teamId ? <TeamScreenActions teamId={teamId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    <TeamsMembersPanel />
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. Only mounts once a team is selected — `TeamReportContent` reads the
              team via `useTeam`, so it must never render without a teamId. With no team this slide-in
              panel is collapsed to width 0 anyway, so rendering nothing here is invisible. */}
          <NavStackScreen index={2} title={<ReportScreenTitle />}>
            <ScrollableContent>
              {hasTeamSelected ? (
                <Suspense fallback={<ReportPanelSkeleton />}>
                  <TeamReportContent />
                </Suspense>
              ) : null}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the TEAM screen (nav stack level 1) — the team's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getTeams`, which the section's loader has already prefetched,
 * so it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function TeamScreenTitle() {
  const { teamId } = useParams({ strict: false })
  if (!teamId) return null
  return (
    <Suspense fallback={null}>
      <TeamName teamId={teamId} />
    </Suspense>
  )
}

function TeamName(props: { teamId: string }) {
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  const team = data.list.find((tm) => tm.id === Number(props.teamId))
  return <>{team?.name ?? ''}</>
}

/**
 * The TEAM screen's header actions: the team's own report (one push deeper, the same level a tapped member
 * lands on but with nobody selected — on desktop that pane is always beside the list, so there is nothing to
 * reach for there) and the owner's action menu.
 */
function TeamScreenActions(props: { teamId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Team report`}
        className="size-8"
        to="/teams/$teamId/report"
        params={{ teamId: props.teamId }}
        search={(prev) => clearProfileSelection(prev)}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <TeamsTitleActionsLoaded />
      </Suspense>
    </>
  )
}

function TeamReportContent() {
  const { t } = useLingui()
  const { first, second } = useRootSearch()
  const { team } = useTeam()

  const hasMembersInTeam = (team?.members?.length ?? 0) > 0
  const hasProfileSelected = first?.type === 'applicant' || second?.type === 'member' || second?.type === 'applicant'

  if (!hasMembersInTeam && !hasProfileSelected) {
    return (
      <ScrollableArea>
        <PanelEmptyState
          icon={<UsersIcon className="text-muted-foreground size-5" />}
          title={t`No members in this team`}
          description={t`Add members to get started`}
        />
      </ScrollableArea>
    )
  }

  return (
    <>
      <StickyHeader>
        <ReportTabs hasMembersInTeam={hasMembersInTeam} team={team} />
      </StickyHeader>
      {/* The `teams.$teamId` layout route renders here through the scroll-snap report pager: the tab
          content (report / distribution / board / …) becomes swipeable panes, in lockstep with the tab
          bar's underline. It replaces the old <AnimatedOutlet/> — the pager owns the per-navigation motion
          now. Sits below the tab bar (a sibling) so the SlidingMarker in AnimatedTabs can measure the tabs
          and ride the same progress. No <ScrollableArea/> wrapper: the pager puts one INSIDE each pane, so
          every tab scrolls on its own (see ReportPager). */}
      <ReportPager team={team} hasMembersInTeam={hasMembersInTeam} className="min-h-0 flex-1" />
    </>
  )
}

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

function TeamsBreadcrumbs() {
  const { t } = useLingui()
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/teams"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {t`Teams`}
              </Link>
            }
          />
        </BreadcrumbItem>
        <Suspense fallback={null}>
          <TeamDetailCrumb />
        </Suspense>
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function TeamDetailCrumb() {
  const { teamId } = useParams({ strict: false })
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  if (!teamId) return null
  const team = data.list.find((tm) => tm.id === Number(teamId))
  if (!team?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/teams/$teamId/report"
              params={{ teamId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {team.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function TeamsTitleActions() {
  return (
    <Suspense fallback={null}>
      <TeamsTitleActionsLoaded />
    </Suspense>
  )
}

function TeamsTitleActionsLoaded() {
  const { teamId } = useParams({ strict: false })
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  if (!teamId || isEmployee) return null
  const selectedTeam = data.list.find((tm) => tm.id === Number(teamId))
  if (!selectedTeam) return null
  return <TeamActionMenu team={selectedTeam} variant="header" />
}

type PanelTeam = {
  id: number
  name: string
  userId: number
  type: 'myTeams' | 'otherTeams'
  membersCount?: number
  function?: number | null
}

// Reads the loader-prefetched `getTeams` (see this route's `loader`) and feeds the rail list view.
function TeamsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  return <TeamsPanelView variant={props.variant} teams={data.list} isEmployee={data.viewerRole === 'member'} />
}

function TeamsPanelView(props: { variant?: 'sidebar' | 'main'; teams: PanelTeam[]; isEmployee: boolean }) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  // Create is for the owner (a company account, or an admin in company view). An `authorized` employee
  // sees the company's teams read-only — the backend also rejects the write (Block A/C).
  const canCreateTeams = usePermissions().can('teams:write')
  const teams = props.teams
  const { teamId } = useParams({ strict: false })
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const navigate = useNavigate()
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside TeamTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  // Clear search when a team is selected so the collapsed rail shows all teams
  useEffect(() => {
    if (teamId) setSearchQuery('')
  }, [teamId])

  const handleCreateTeam = () => {
    navigate({ to: '.', search: (prev) => ({ ...prev, editTeam: 'new' }) })
  }

  const filtered = teams.filter((team) => team.name.toLowerCase().includes(searchQuery.toLowerCase()))

  const myTeams = filtered.filter((team) => team.type === 'myTeams')
  const otherTeams = filtered.filter((team) => team.type === 'otherTeams')
  const allTeams = [...myTeams, ...otherTeams]
  const allTeamsCount = allTeams.length
  const myTeamsCount = myTeams.length
  const otherTeamsCount = otherTeams.length

  const showOnlyEmptyState = teams.length === 0 && !searchQuery

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    // Main panel variant - full UI adapted for main content area
    return (
      <div className="flex h-full flex-col">
        {!showOnlyEmptyState && (
          <>
            {/* Search bar with create button. Employees are view-only — they can't create teams. */}
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
              {!isEmployee && canCreateTeams && (
                <Button variant="secondary" onClick={handleCreateTeam}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Teams list */}
        <ScrollableList
          isEmpty={allTeams.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allTeams.length === 0 && !!searchQuery}
              icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
              title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
              description={isEmployee ? undefined : t`Add teams to get started`}
              action={
                !isEmployee && canCreateTeams ? (
                  <Button variant="secondary" onClick={handleCreateTeam}>
                    <PlusIcon className="size-4" />
                    {t`Add team`}
                  </Button>
                ) : undefined
              }
            />
          }
          hint={
            allTeams.length > 0 && (
              <SelectionHint visible={stacked || !teamId} text={t`Select a team to view its members and reports`} />
            )
          }
        >
          <SectionLabel className="px-2">
            {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
          </SectionLabel>
          {isEmployee ? (
            // Employees don't own teams in this version, so the "my teams" vs "teams that added me"
            // split is meaningless to them — show every assigned team as a single flat list.
            allTeams.map((team) => (
              <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
            ))
          ) : (
            <>
              {/* My Teams section */}
              {myTeams.length > 0 && (
                <>
                  {otherTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                      </SectionLabel>
                    </div>
                  )}
                  {myTeams.map((team) => (
                    <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                  ))}
                </>
              )}

              {/* Other Teams section */}
              {otherTeams.length > 0 && (
                <>
                  {myTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {otherTeams.length === 1
                          ? t`${otherTeamsCount} team that added me`
                          : t`${otherTeamsCount} teams that added me`}
                      </SectionLabel>
                    </div>
                  )}
                  {otherTeams.map((team) => (
                    <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                  ))}
                </>
              )}
            </>
          )}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search onSearchClick={() => setSearchOpen(true)} searchLabel={t`Search Teams`}>
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
            </CollapsibleToolbar.Search>
            {/* Employees are view-only — no create action. */}
            {!isEmployee && canCreateTeams && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                label={t`Add`}
                tooltip={t`Add team`}
                onClick={handleCreateTeam}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isEmpty={allTeams.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allTeams.length === 0 && !!searchQuery}
            icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
            title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
            description={isEmployee ? undefined : t`Add teams to get started`}
            action={
              !isEmployee && canCreateTeams ? (
                <Button variant="secondary" onClick={handleCreateTeam}>
                  <PlusIcon className="size-4" />
                  {t`Add team`}
                </Button>
              ) : undefined
            }
          />
        }
        hint={
          allTeams.length > 0 && (
            <SelectionHint visible={stacked || !teamId} text={t`Select a team to view its members and reports`} />
          )
        }
      >
        {isEmployee ? (
          // Employees don't own teams — show every assigned team in one flat list (no my/other split).
          <>
            <CollapsibleSectionLabel>
              {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
            </CollapsibleSectionLabel>
            {allTeams.map((team) => (
              <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
            ))}
          </>
        ) : (
          <>
            {myTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                </CollapsibleSectionLabel>
                {myTeams.map((team) => (
                  <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                ))}
              </>
            )}

            {otherTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {otherTeams.length === 1
                    ? t`${otherTeamsCount} team that added me`
                    : t`${otherTeamsCount} teams that added me`}
                </CollapsibleSectionLabel>
                {otherTeams.map((team) => (
                  <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                ))}
              </>
            )}
          </>
        )}
      </ScrollableList>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} title={t`Search Teams`}>
        <Command className="gap-1">
          <CommandInput placeholder={t`Search Teams`} />
          <CommandList>
            <CommandEmpty>{t`No teams found`}</CommandEmpty>
            {allTeams.map((team) => (
              <CommandItem
                key={team.id}
                value={`${team.name} ${team.id}`}
                onSelect={() => {
                  navigate({
                    to: '/teams/$teamId/report',
                    params: { teamId: team.id.toString() },
                    search: (prev) => ({ ...clearProfileSelection(prev), q: undefined }),
                  })
                  setSearchOpen(false)
                }}
              >
                <Avatar size="sm" type="team" name={team.name} />
                <span className="truncate">{team.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}
